import { useState, useEffect } from 'react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import apiClient from '../../services/api';

interface InvitationOverview {
  total_invitations: number;
  active_invitations: number;
  total_invited_users: number;
  active_invited_users: number;
  total_invitation_codes: number;
  active_invitation_codes: number;
}

interface InvitationCode {
  id: number;
  user_id: number;
  user_email: string;
  code: string;
  status: string;
  use_count: number;
  max_uses: number | null;
  created_at: string;
}

interface InvitedUser {
  id: number;
  email: string;
  status: string;
  balance: number;
  registered_at: string;
  inviter_id: number;
  inviter_email: string;
  invitation_code: string;
}

interface TopInviter {
  user_id: number;
  email: string;
  invitation_count: number;
  active_invitation_count: number;
}

export function AdminInvitationPage() {
  const [overview, setOverview] = useState<InvitationOverview | null>(null);
  const [codes, setCodes] = useState<InvitationCode[]>([]);
  const [invitedUsers, setInvitedUsers] = useState<InvitedUser[]>([]);
  const [topInviters, setTopInviters] = useState<TopInviter[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchOverview();
  }, []);

  useEffect(() => {
    if (activeTab === 'codes') {
      fetchCodes();
    } else if (activeTab === 'invited-users') {
      fetchInvitedUsers();
    } else if (activeTab === 'top-inviters') {
      fetchTopInviters();
    }
  }, [activeTab, page, pageSize]);

  const fetchOverview = async () => {
    try {
      const response = await apiClient.get('/admin/invitations/overview');
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch invitation overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCodes = async () => {
    try {
      const response = await apiClient.get('/admin/invitations/codes', {
        params: { page, page_size: pageSize }
      });
      setCodes(response.data.codes);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch invitation codes:', error);
    }
  };

  const fetchInvitedUsers = async () => {
    try {
      const response = await apiClient.get('/admin/invitations/invited-users', {
        params: { page, page_size: pageSize }
      });
      setInvitedUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch invited users:', error);
    }
  };

  const fetchTopInviters = async () => {
    try {
      const response = await apiClient.get('/admin/invitations/top-inviters');
      setTopInviters(response.data);
    } catch (error) {
      console.error('Failed to fetch top inviters:', error);
    }
  };

  const handleUpdateCodeStatus = async (id: number, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
    if (!confirm(`确定要将邀请码状态改为 ${newStatus === 'active' ? '活跃' : '禁用'} 吗？`)) {
      return;
    }
    try {
      await apiClient.put(`/admin/invitations/codes/${id}/status`, { status: newStatus });
      fetchCodes();
    } catch (error) {
      console.error('Failed to update code status:', error);
      alert('更新状态失败');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      disabled: 'bg-gray-100 text-gray-800',
    };
    const labels: Record<string, string> = {
      active: '活跃',
      disabled: '禁用',
    };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <h3 className="text-sm font-medium text-gray-500">总邀请次数</h3>
          <p className="mt-2 text-3xl font-bold text-blue-600">
            {overview?.total_invitations?.toLocaleString() || 0}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            活跃: {overview?.active_invitations?.toLocaleString() || 0}
          </p>
        </Card>

        <Card className="p-6">
          <h3 className="text-sm font-medium text-gray-500">总邀请用户数</h3>
          <p className="mt-2 text-3xl font-bold text-green-600">
            {overview?.total_invited_users?.toLocaleString() || 0}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            活跃: {overview?.active_invited_users?.toLocaleString() || 0}
          </p>
        </Card>

        <Card className="p-6">
          <h3 className="text-sm font-medium text-gray-500">邀请码数量</h3>
          <p className="mt-2 text-3xl font-bold text-purple-600">
            {overview?.total_invitation_codes?.toLocaleString() || 0}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            活跃: {overview?.active_invitation_codes?.toLocaleString() || 0}
          </p>
        </Card>
      </div>

      <Card className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">邀请统计概览</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">邀请成功率</h4>
            <p className="text-2xl font-bold text-orange-600">
              {overview && overview.total_invitations > 0 
                ? `${Math.round((overview.active_invitations / overview.total_invitations) * 100)}%` 
                : '0%'}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">用户活跃率</h4>
            <p className="text-2xl font-bold text-teal-600">
              {overview && overview.total_invited_users > 0 
                ? `${Math.round((overview.active_invited_users / overview.total_invited_users) * 100)}%` 
                : '0%'}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderCodes = () => (
    <Card>
      <div className="flex justify-between items-center p-6 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">邀请码管理</h3>
        <Button onClick={fetchCodes}>刷新</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">ID</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">用户邮箱</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">邀请码</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">状态</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">使用次数</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">创建时间</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">操作</th>
            </tr>
          </thead>
          <tbody>
            {codes.map((code) => (
              <tr key={code.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4 text-sm">{code.id}</td>
                <td className="py-3 px-4 text-sm">{code.user_email}</td>
                <td className="py-3 px-4 text-sm font-mono">{code.code}</td>
                <td className="py-3 px-4">
                  {getStatusBadge(code.status)}
                </td>
                <td className="py-3 px-4 text-sm">
                  {code.use_count} / {code.max_uses || '无限制'}
                </td>
                <td className="py-3 px-4 text-sm">{code.created_at}</td>
                <td className="py-3 px-4">
                  <Button
                    size="sm"
                    variant={code.status === 'active' ? 'secondary' : 'primary'}
                    onClick={() => handleUpdateCodeStatus(code.id, code.status)}
                  >
                    {code.status === 'active' ? '禁用' : '启用'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-6 border-t border-gray-200 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          共 {total} 条记录
        </div>
        <div className="flex space-x-2">
          <Button
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            上一页
          </Button>
          <Button
            size="sm"
            disabled={page * pageSize >= total}
            onClick={() => setPage(page + 1)}
          >
            下一页
          </Button>
        </div>
      </div>
    </Card>
  );

  const renderInvitedUsers = () => (
    <Card>
      <div className="flex justify-between items-center p-6 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">邀请用户管理</h3>
        <Button onClick={fetchInvitedUsers}>刷新</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">ID</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">邮箱</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">状态</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">余额</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">注册时间</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">邀请人</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">邀请码</th>
            </tr>
          </thead>
          <tbody>
            {invitedUsers.map((user) => (
              <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4 text-sm">{user.id}</td>
                <td className="py-3 px-4 text-sm">{user.email}</td>
                <td className="py-3 px-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    user.status === 'active' ? 'bg-green-100 text-green-800' :
                    user.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {user.status === 'active' ? '活跃' :
                     user.status === 'suspended' ? '已暂停' :
                     '已删除'}
                  </span>
                </td>
                <td className="py-3 px-4 text-sm">${user.balance?.toFixed(2) || '0.00'}</td>
                <td className="py-3 px-4 text-sm">{user.registered_at}</td>
                <td className="py-3 px-4 text-sm">{user.inviter_email}</td>
                <td className="py-3 px-4 text-sm font-mono">{user.invitation_code}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-6 border-t border-gray-200 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          共 {total} 条记录
        </div>
        <div className="flex space-x-2">
          <Button
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            上一页
          </Button>
          <Button
            size="sm"
            disabled={page * pageSize >= total}
            onClick={() => setPage(page + 1)}
          >
            下一页
          </Button>
        </div>
      </div>
    </Card>
  );

  const renderTopInviters = () => (
    <Card>
      <div className="flex justify-between items-center p-6 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">邀请排行榜</h3>
        <Button onClick={fetchTopInviters}>刷新</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">排名</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">用户邮箱</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">邀请总数</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">活跃邀请数</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">活跃率</th>
            </tr>
          </thead>
          <tbody>
            {topInviters.map((inviter, index) => (
              <tr key={inviter.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4 text-sm font-medium">
                  {index + 1}
                </td>
                <td className="py-3 px-4 text-sm">{inviter.email}</td>
                <td className="py-3 px-4 text-sm">{inviter.invitation_count}</td>
                <td className="py-3 px-4 text-sm">{inviter.active_invitation_count}</td>
                <td className="py-3 px-4 text-sm">
                  {inviter.invitation_count > 0 
                    ? `${Math.round((inviter.active_invitation_count / inviter.invitation_count) * 100)}%` 
                    : '0%'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">邀请管理</h1>

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('overview');
              setPage(1);
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            概览
          </button>
          <button
            onClick={() => {
              setActiveTab('codes');
              setPage(1);
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'codes' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            邀请码管理
          </button>
          <button
            onClick={() => {
              setActiveTab('invited-users');
              setPage(1);
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'invited-users' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            邀请用户管理
          </button>
          <button
            onClick={() => {
              setActiveTab('top-inviters');
              setPage(1);
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'top-inviters' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            邀请排行榜
          </button>
        </nav>
      </div>

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'codes' && renderCodes()}
      {activeTab === 'invited-users' && renderInvitedUsers()}
      {activeTab === 'top-inviters' && renderTopInviters()}
    </div>
  );
}
